import React from 'react';
import './terms.css';

const tocItems = [
  { id: 'refund-policy', label: 'Refund Policy' },
  { id: 'fee-guidelines', label: 'Fee & Payment Guidelines' },
  { id: 'conduct', label: 'Code of Conduct' },
];

const Terms: React.FC = () => {
  return (
    <div className="terms-page">
      <section className="terms-hero">
        <div className="hero-path">
          <span>Home / Legal / Terms and Conditions</span>
        </div>
        <div className="hero-title-group">
          <h1>Terms and Conditions</h1>
          <p>S-CUBUS CAREER Pvt. Ltd.</p>
        </div>
      </section>

      <main className="terms-body">
        <aside className="toc">
          <div className="toc-title">Table of Contents</div>
          <ul>
            {tocItems.map((item, index) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </aside>

        <section className="terms-content">
          <article id="refund-policy" className="terms-section">
            <div className="section-meta">
              <span className="section-number">01</span>
              <h2>S-CUBUS CAREER Pvt. Ltd. Refund Policy</h2>
            </div>

            <p className="terms-kicker">We Believe in Fairness & Transparency</p>
            <p>
              At S-CUBUS CAREER Pvt. Ltd., we maintain a clear and student-friendly refund policy to ensure smooth
              processing in case a student wishes to discontinue the course after admission.
            </p>

            <h3 className="terms-subtitle">A) Fees Components & Refund Applicability</h3>
            <table className="terms-table">
              <thead>
                <tr>
                  <th>Fee Component</th>
                  <th>Refund Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Registration Fee</td>
                  <td>Non-Refundable</td>
                </tr>
                <tr>
                  <td>Admission Kit + Digital Access Fee</td>
                  <td>Non-Refundable (once activated/issued)</td>
                </tr>
                <tr>
                  <td>Tuition Fee</td>
                  <td>Refundable as per refund schedule</td>
                </tr>
                <tr>
                  <td>Technology / LMS Fee</td>
                  <td>Non-Refundable</td>
                </tr>
                <tr>
                  <td>Exam / Test Fee (if applicable)</td>
                  <td>Non-Refundable once scheduled/attempted</td>
                </tr>
              </tbody>
            </table>

            <h3 className="terms-subtitle">B) Refund Schedule (Based on Withdrawal Request Date)</h3>
            <p>Refund is calculated from the Batch Start Date or Registration Date, whichever is later.</p>
            <table className="terms-table">
              <thead>
                <tr>
                  <th>Time of Withdrawal Request</th>
                  <th>Refund on Tuition</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Before Batch Commencement</td>
                  <td>100% Refundable</td>
                </tr>
                <tr>
                  <td>Week 1</td>
                  <td>75% Refundable (25% of 1st installment deducted)</td>
                </tr>
                <tr>
                  <td>Week 2</td>
                  <td>50% Refundable (50% of 1st installment deducted)</td>
                </tr>
                <tr>
                  <td>Week 3</td>
                  <td>25% Refundable (75% of 1st installment deducted)</td>
                </tr>
                <tr>
                  <td>Week 4 or Later</td>
                  <td>No Refund (Tuition/Tech not refundable)</td>
                </tr>
              </tbody>
            </table>
            <p className="terms-note">
              *Registration Fee, Technology/LMS Fee and Admission Kit/Digital Access Fee are non-refundable in all
              cases.
            </p>

            <h3 className="terms-subtitle">C) Key Things for Calculation of Refund</h3>
            <table className="terms-table">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>What will be considered?</th>
                  <th>What will NOT be considered?</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Batch Start Date / Registration Date (whichever is later)</td>
                  <td>Date when student attended the first class</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Date of submission of withdrawal/refund request</td>
                  <td>Date of last class attended by student</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Refund calculation based on net fee paid (after scholarship/discount)</td>
                  <td>Printed fee value before scholarship/discount</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>Installments due up to refund request date (if any)</td>
                  <td>Future installments beyond the refund request date</td>
                </tr>
              </tbody>
            </table>

            <h3 className="terms-subtitle">Illustration (Sample Dates for Refund Calculation)</h3>
            <table className="terms-table">
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>For the Purpose of Refund Calculation</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>a</td>
                  <td>Registration fee payment date</td>
                  <td>8th May</td>
                </tr>
                <tr>
                  <td>b</td>
                  <td>Date of batch commencement</td>
                  <td>10th May</td>
                </tr>
                <tr>
                  <td>c</td>
                  <td>Student actually joined the class</td>
                  <td>16th May</td>
                </tr>
                <tr>
                  <td>d</td>
                  <td>Last class attended</td>
                  <td>14th Aug</td>
                </tr>
                <tr>
                  <td>e</td>
                  <td>Submission of withdrawal/refund application</td>
                  <td>13th Sep</td>
                </tr>
                <tr>
                  <td>f</td>
                  <td>Number of days/services consumed shall be calculated as</td>
                  <td>10th May - 13th Sep</td>
                </tr>
                <tr>
                  <td>g</td>
                  <td>Refund will NOT be calculated from</td>
                  <td>16th May - 13th Sep OR 8th May - 13th Sep</td>
                </tr>
              </tbody>
            </table>

            <h3 className="terms-subtitle">D) No Refund Cases</h3>
            <ul className="terms-list">
              <li>Registration Fee is not refundable under any circumstances.</li>
              <li>Admission Kit / Digital Access Fee is not refundable once issued/activated.</li>
              <li>If withdrawal is requested after Week 4 (or later), tuition fee is not refundable.</li>
              <li>Any fee paid towards tests/exams once scheduled or attempted will not be refunded.</li>
              <li>
                In case the government imposes lockdown/passes order causing restriction on offline classes, classes
                may shift online. Fee adjustment, if any, will be at management discretion.
              </li>
            </ul>

            <h3 className="terms-subtitle">E) Mode of Refund</h3>
            <p>All refunds will be processed via NEFT/Online Bank Transfer only. Cash refunds are not permitted.</p>
            <p className="terms-note">
              Disclaimer: S-CUBUS CAREER Pvt. Ltd. reserves the right to modify the refund policy as per operational
              requirements. Final approval is subject to verification and management discretion.
            </p>
          </article>

          <article id="fee-guidelines" className="terms-section">
            <div className="section-meta">
              <span className="section-number">02</span>
              <h2>S-Cubus Career Pvt. Ltd. Fee & Payment Guidelines</h2>
            </div>

            <table className="terms-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Guideline Title</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Fee Commitment</td>
                  <td>
                    Parents/guardians are requested to complete fee payments within the shared timelines to ensure
                    uninterrupted academic support.
                  </td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Understanding the Guidelines</td>
                  <td>
                    Submission of the admission form indicates acceptance of the academic practices and learning
                    environment of S-CUBUS.
                  </td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Payment Process</td>
                  <td>
                    Admission is confirmed once the fee payment is successfully processed through approved payment
                    modes.
                  </td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>Academic Interaction</td>
                  <td>
                    Parents may connect with faculty or academic coordinators with prior intimation for meaningful
                    discussion regarding student progress.
                  </td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>Contact Information</td>
                  <td>Accurate and updated contact details help the institute share important academic updates smoothly.</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>Attendance & Learning Discipline</td>
                  <td>
                    Regular attendance is encouraged for better learning outcomes. Students should inform the institute
                    in case of unavoidable absence.
                  </td>
                </tr>
                <tr>
                  <td>7</td>
                  <td>Communication Flow</td>
                  <td>
                    All notices, test results, and academic updates are shared through the registered contact details.
                  </td>
                </tr>
                <tr>
                  <td>8</td>
                  <td>Shared Responsibility</td>
                  <td>
                    Academic success is best achieved through teamwork between students, parents, and teachers in a
                    respectful environment.
                  </td>
                </tr>
              </tbody>
            </table>
          </article>

          <article id="conduct" className="terms-section">
            <div className="section-meta">
              <span className="section-number">03</span>
              <h2>S-Cubus Career Pvt. Ltd. Code of Conduct for Students & Parents</h2>
            </div>

            <h3 className="terms-subtitle">Uniform & Identity</h3>
            <p>
              Students must wear the prescribed S-CUBUS uniform and carry a valid ID card daily. Entry without ID is
              not permitted.
            </p>

            <h3 className="terms-subtitle">Attendance & Punctuality</h3>
            <p>
              Students are expected to attend classes regularly and reach on time. Late entry beyond the permitted
              grace period may not be allowed.
            </p>

            <h3 className="terms-subtitle">Campus Exit</h3>
            <p>Students should remain on campus during class hours. Exit is permitted only with prior approval and a valid gate pass.</p>

            <h3 className="terms-subtitle">Lunch Guidelines</h3>
            <p>Students should bring lunch from home. Ordering food from outside delivery services during class hours is not allowed.</p>

            <h3 className="terms-subtitle">Discipline & Behaviour</h3>
            <p>
              Students must maintain discipline, respectful behaviour, and protect institute property. Misconduct or
              damage may lead to disciplinary action.
            </p>

            <h3 className="terms-subtitle">Mobile Phone Usage</h3>
            <p>Use of mobile phones during class hours is prohibited unless permitted by faculty.</p>

            <h3 className="terms-subtitle">Fee Responsibility</h3>
            <p>Parents are responsible for timely payment of fees as per the schedule shared at admission.</p>

            <h3 className="terms-subtitle">Communication Details</h3>
            <p>Parents and students must provide correct contact details for official communication.</p>

            <h3 className="terms-subtitle">Eligibility & Applications</h3>
            <p>
              Students are responsible for checking eligibility and submitting entrance exam forms. The institute is
              not responsible for application outcomes.
            </p>

            <h3 className="terms-subtitle">Force Majeure</h3>
            <p>
              The institute is not liable for disruptions due to events beyond control such as natural calamities or
              government restrictions. Classes may shift online.
            </p>

            <h3 className="terms-subtitle">GST & Taxes</h3>
            <p>Any change in government taxes or GST during the course period shall be payable by the student or parent.</p>

            <h3 className="terms-subtitle">Publicity & Achievements</h3>
            <p>
              Student achievements or testimonials may be used for academic or promotional purposes with appropriate
              consent.
            </p>

            <h3 className="terms-subtitle">Refund Policy Reference</h3>
            <p>Refunds shall be governed strictly by the official S-CUBUS Refund Policy provided at the time of admission.</p>

            <p className="terms-note">
              Note: This Code of Conduct is intended to ensure a safe, disciplined, and productive learning
              environment. S-Cubus Career Pvt. Ltd. reserves the right to amend this code when necessary.
            </p>
          </article>
        </section>
      </main>
    </div>
  );
};

export default Terms;
